import { __extends } from "tslib";
import { EffectEnvMapMethod, _Shader_EffectEnvMapMethod } from "./EffectEnvMapMethod";
/**
 * EffectRefractionEnvMapMethod provides a method to add refracted transparency based on cube maps.
 */
var EffectRefractionEnvMapMethod = /** @class */ (function (_super) {
    __extends(EffectRefractionEnvMapMethod, _super);
    /**
     * Creates a new EffectRefractionEnvMapMethod object. Example values for dispersion are: dispersionR: -0.03, dispersionG: -0.01, dispersionB: = .0015
     *
     * @param envMap The environment map containing the refracted scene.
     * @param refractionIndex The refractive index of the material.
     * @param dispersionR The amount of chromatic dispersion of the red channel. Defaults to 0 (none).
     * @param dispersionG The amount of chromatic dispersion of the green channel. Defaults to 0 (none).
     * @param dispersionB The amount of chromatic dispersion of the blue channel. Defaults to 0 (none).
     */
    function EffectRefractionEnvMapMethod(envMap, alpha, refractionIndex, dispersionR, dispersionG, dispersionB) {
        if (alpha === void 0) { alpha = 1; }
        if (refractionIndex === void 0) { refractionIndex = .1; }
        if (dispersionR === void 0) { dispersionR = 0; }
        if (dispersionG === void 0) { dispersionG = 0; }
        if (dispersionB === void 0) { dispersionB = 0; }
        var _this = _super.call(this, envMap, alpha) || this;
        _this._dispersionR = 0;
        _this._dispersionG = 0;
        _this._dispersionB = 0;
        _this._refractionIndex = refractionIndex;
        _this._dispersionR = dispersionR;
        _this._dispersionG = dispersionG;
        _this._dispersionB = dispersionB;
        return _this;
    }
    Object.defineProperty(EffectRefractionEnvMapMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return EffectRefractionEnvMapMethod.assetType;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EffectRefractionEnvMapMethod.prototype, "refractionIndex", {
        /**
         * The refractive index of the material.
         */
        get: function () {
            return this._refractionIndex;
        },
        set: function (value) {
            this._refractionIndex = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EffectRefractionEnvMapMethod.prototype, "dispersionR", {
        /**
         * The amount of chromatic dispersion of the red channel. Defaults to 0 (none).
         */
        get: function () {
            return this._dispersionR;
        },
        set: function (value) {
            if (this._dispersionR == value)
                return;
            this._dispersionR = value;
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EffectRefractionEnvMapMethod.prototype, "dispersionG", {
        /**
         * The amount of chromatic dispersion of the green channel. Defaults to 0 (none).
         */
        get: function () {
            return this._dispersionG;
        },
        set: function (value) {
            if (this._dispersionG == value)
                return;
            this._dispersionG = value;
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EffectRefractionEnvMapMethod.prototype, "dispersionB", {
        /**
         * The amount of chromatic dispersion of the blue channel. Defaults to 0 (none).
         */
        get: function () {
            return this._dispersionB;
        },
        set: function (value) {
            if (this._dispersionB == value)
                return;
            this._dispersionB = value;
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    EffectRefractionEnvMapMethod.assetType = "[asset EffectRefractionEnvMapMethod]";
    return EffectRefractionEnvMapMethod;
}(EffectEnvMapMethod));
export { EffectRefractionEnvMapMethod };
import { ShaderBase } from "@awayjs/renderer";
/**
 * EffectRefractionEnvMapMethod provides a method to add refracted transparency based on cube maps.
 */
var _Shader_EffectRefractionEnvMapMethod = /** @class */ (function (_super) {
    __extends(_Shader_EffectRefractionEnvMapMethod, _super);
    function _Shader_EffectRefractionEnvMapMethod() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * @inheritDoc
     */
    _Shader_EffectRefractionEnvMapMethod.prototype._initVO = function (chunkVO) {
        _super.prototype._initVO.call(this, chunkVO);
        this._useDispersion = !(this._method.dispersionR == this._method.dispersionB && this._method.dispersionR == this._method.dispersionG);
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectRefractionEnvMapMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        // todo: data2.x could use common reg, so only 1 reg is used
        var data = registerCache.getFreeFragmentConstant();
        var data2 = registerCache.getFreeFragmentConstant();
        var code = "";
        var refractionDir;
        var refractionColor;
        var temp;
        this._fragmentIndex = data.index * 4;
        refractionDir = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(refractionDir, 1);
        refractionColor = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(refractionColor, 1);
        temp = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(temp, 1);
        var viewDirReg = sharedRegisters.viewDirFragment;
        var normalReg = sharedRegisters.normalFragment;
        code += "neg " + viewDirReg + ".xyz, " + viewDirReg + ".xyz\n";
        code += "dp3 " + temp + ".x, " + viewDirReg + ".xyz, " + normalReg + ".xyz\n" +
            "mul " + temp + ".w, " + temp + ".x, " + temp + ".x\n" +
            "sub " + temp + ".w, " + data2 + ".x, " + temp + ".w\n" +
            "mul " + temp + ".w, " + data + ".x, " + temp + ".w\n" +
            "mul " + temp + ".w, " + data + ".x, " + temp + ".w\n" +
            "sub " + temp + ".w, " + data2 + ".x, " + temp + ".w\n" +
            "sqt " + temp + ".y, " + temp + ".w\n" +
            "mul " + temp + ".x, " + data + ".x, " + temp + ".x\n" +
            "add " + temp + ".x, " + temp + ".x, " + temp + ".y\n" +
            "mul " + temp + ".xyz, " + temp + ".x, " + normalReg + ".xyz\n" +
            "mul " + refractionDir + ", " + data + ".x, " + viewDirReg + "\n" +
            "sub " + refractionDir + ".xyz, " + refractionDir + ".xyz, " + temp + ".xyz\n" +
            "nrm " + refractionDir + ".xyz, " + refractionDir + ".xyz\n" +
            this._envMap._getFragmentCode(refractionColor, registerCache, sharedRegisters, refractionDir) +
            "sub " + refractionColor + ".w, " + refractionColor + ".w, fc0.x	\n" +
            "kil " + refractionColor + ".w\n";
        if (this._useDispersion) {
            // GREEN
            code += "dp3 " + temp + ".x, " + viewDirReg + ".xyz, " + normalReg + ".xyz\n" +
                "mul " + temp + ".w, " + temp + ".x, " + temp + ".x\n" +
                "sub " + temp + ".w, " + data2 + ".x, " + temp + ".w\n" +
                "mul " + temp + ".w, " + data + ".y, " + temp + ".w\n" +
                "mul " + temp + ".w, " + data + ".y, " + temp + ".w\n" +
                "sub " + temp + ".w, " + data2 + ".x, " + temp + ".w\n" +
                "sqt " + temp + ".y, " + temp + ".w\n" +
                "mul " + temp + ".x, " + data + ".y, " + temp + ".x\n" +
                "add " + temp + ".x, " + temp + ".x, " + temp + ".y\n" +
                "mul " + temp + ".xyz, " + temp + ".x, " + normalReg + ".xyz\n" +
                "mul " + refractionDir + ", " + data + ".y, " + viewDirReg + "\n" +
                "sub " + refractionDir + ".xyz, " + refractionDir + ".xyz, " + temp + ".xyz\n" +
                "nrm " + refractionDir + ".xyz, " + refractionDir + ".xyz\n" +
                this._envMap._getFragmentCode(temp, registerCache, sharedRegisters, refractionDir) +
                "mov " + refractionColor + ".y, " + temp + ".y\n";
            // BLUE
            code += "dp3 " + temp + ".x, " + viewDirReg + ".xyz, " + normalReg + ".xyz\n" +
                "mul " + temp + ".w, " + temp + ".x, " + temp + ".x\n" +
                "sub " + temp + ".w, " + data2 + ".x, " + temp + ".w\n" +
                "mul " + temp + ".w, " + data + ".z, " + temp + ".w\n" +
                "mul " + temp + ".w, " + data + ".z, " + temp + ".w\n" +
                "sub " + temp + ".w, " + data2 + ".x, " + temp + ".w\n" +
                "sqt " + temp + ".y, " + temp + ".w\n" +
                "mul " + temp + ".x, " + data + ".z, " + temp + ".x\n" +
                "add " + temp + ".x, " + temp + ".x, " + temp + ".y\n" +
                "mul " + temp + ".xyz, " + temp + ".x, " + normalReg + ".xyz\n" +
                "mul " + refractionDir + ", " + data + ".z, " + viewDirReg + "\n" +
                "sub " + refractionDir + ".xyz, " + refractionDir + ".xyz, " + temp + ".xyz\n" +
                "nrm " + refractionDir + ".xyz, " + refractionDir + ".xyz\n" +
                this._envMap._getFragmentCode(temp, registerCache, sharedRegisters, refractionDir) +
                "mov " + refractionColor + ".z, " + temp + ".z\n";
        }
        code += "sub " + refractionColor + ".xyz, " + refractionColor + ".xyz, " + targetReg + ".xyz\n" +
            "mul " + refractionColor + ".xyz, " + refractionColor + ".xyz, " + data + ".w\n" +
            "add " + targetReg + ".xyz, " + targetReg + ".xyz, " + refractionColor + ".xyz\n";
        registerCache.removeFragmentTempUsage(temp);
        registerCache.removeFragmentTempUsage(refractionDir);
        registerCache.removeFragmentTempUsage(refractionColor);
        // restore
        code += "neg " + viewDirReg + ".xyz, " + viewDirReg + ".xyz\n";
        return code;
    };
    _Shader_EffectRefractionEnvMapMethod.prototype._updateProperties = function () {
        var index = this._fragmentIndex;
        var data = this._shader.fragmentConstantData;
        var refractionIndex = this._method.refractionIndex;
        data[index] = this._method.dispersionR + refractionIndex;
        if (this._useDispersion) {
            data[index + 1] = this._method.dispersionG + refractionIndex;
            data[index + 2] = this._method.dispersionB + refractionIndex;
        }
        data[index + 3] = this._method.alpha;
    };
    return _Shader_EffectRefractionEnvMapMethod;
}(_Shader_EffectEnvMapMethod));
export { _Shader_EffectRefractionEnvMapMethod };
ShaderBase.registerAbstraction(_Shader_EffectRefractionEnvMapMethod, EffectRefractionEnvMapMethod);
